import React, { useEffect, useReducer, useState, useMemo } from "react";
import client from "../../../../helpers/Client";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  Stack,
  Text,
  VStack,
  Icon,
  InputGroup,
  InputLeftElement,
  Fade,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { FiHome, FiUser, FiPhone, FiMail, FiMapPin, FiHash, FiSettings } from "react-icons/fi";

import "../../../Public/Support/style.css";
import urlToId from "../../../../helpers/UrlToId";
import { useNavigate } from "react-router-dom";
import { translateProjectKind } from "../../../../shared/libs/translate";
import MapBoxField from "../../../../components/map/MapBoxField";

export function AddProject({ disclosure }) {
  const cardColor = "gray.50";
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Modern color mode values
  const bgColor = 'white';
  const borderColor = 'gray.200';
  const inputBg = 'gray.50';
  const inputFocusBg = 'white';
  const textColor = 'gray.800';
  const placeholderColor = 'gray.400';
  const [projectName, setProjectName] = useState("");
  const [kind, setKind] = useState("");
  const [numOfUnits, setNumOfUnits] = useState("");
  const [address, setAddress] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [errorData, setErrorData] = useState({});
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");

  const [countries, setCountries] = useState([]); // Initialize with empty array
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const [mode, switchMode] = useReducer((state) => {
    return !state ? "change" : null;
  }, null);
  const navigate = useNavigate();

  // Basic client-side validation
  const validateForm = () => {
    const errors = {};

    if (!projectName.trim()) errors.name = ["Proje adı gereklidir."];
    if (!kind) errors.kind = ["Proje tipi seçilmelidir."];
    if (!numOfUnits) errors.nof_units = ["Daire sayısı gereklidir."];
    if (!district) errors.district = ["İlçe seçilmelidir."];
    if (!address.trim()) errors.address = ["Adres gereklidir."];

    if (contactEmail && !/^\S+@\S+\.\S+$/.test(contactEmail)) {
      errors.contact_email = ["Geçerli bir e-posta adresi giriniz."];
    }
    if (contactPhone && !/^[0-9]{10}$/.test(contactPhone)) {
      errors.contact_phone = ["Telefon numarası 10 haneli olmalıdır (5xxxxxxxxx)."];
    }
    return errors;
  };

  const handleProjectCreate = () => {
    // Clear previous errors
    setErrorData({});

    // Validate form before submission
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrorData(validationErrors);
      toast({
        title: "Form Hatası",
        description: "Lütfen gerekli alanları doldurun.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    const requestBody = {
      name: projectName,
      kind: kind,
      nof_units: numOfUnits,
      district: district,
      address: address,
      contact_name: contactName,
      contact_phone: contactPhone ? "+90" + contactPhone : null,
      contact_email: contactEmail,
    };

    client
      .post(`/projects/`, requestBody)
      .then((response) => {
        toast({
          title: "Başarılı!",
          description: "Proje başarıyla oluşturuldu.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        navigate("/proje/" + urlToId(response.data.url) + "/");
      })
      .catch((error) => {
        setErrorData(error.response?.data || { general: ["Bir hata oluştu."] });
        toast({
          title: "Hata!",
          description: "Proje oluşturulurken bir hata oluştu.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (mode === "change") switchMode();
    setCountry("");
    setCity("");
  }, [mode]);

  useEffect(() => {
    client.get("/public/countries/").then((resp) => {
      setCountries(resp.data);
    }).catch(error => {
      console.error("Countries fetch error:", error);
    });
  }, []);

  useEffect(() => {
    if (!countries.length) return;

    const ulke = params.get('ulke');
    if (ulke) {
      const matchingCountry = countries.find(country => urlToId(country.url) === ulke);
      if (matchingCountry) {
        setCountry(matchingCountry.url);
      }
    } else if (countries.length === 1) {
      // Only auto-select if there's exactly one country
      setCountry(countries[0].url);
    }
  }, [countries, params]);

  useEffect(() => {
    if (!cities.length) return;

    const il = params.get('il');
    if (il) {
      const matchingCity = cities.find(city => urlToId(city.url) === il);
      if (matchingCity) {
        setCity(matchingCity.url);
      }
    } else if (cities.length === 1) {
      // Only auto-select if there's exactly one city
      setCity(cities[0].url);
    }
  }, [cities, params]);

  useEffect(() => {
    if (!districts.length) return;
    const ilce = params.get('ilce');
    if (ilce) {
      const matchingDistrict = districts.find(district => urlToId(district.url) === ilce);
      if (matchingDistrict) {
        setDistrict(matchingDistrict.url);
      }
    } else if (districts.length === 1) {
      setDistrict(districts[0].url);
    }
  }, [districts, params])

  useEffect(() => {
    setCity("");
    setDistrict("");
    if (!country) {
      setCities([]);
      return;
    }
    client
      .get(`/public/cities/?country=${urlToId(country)}`)
      .then((resp) => setCities(resp.data))
      .catch(error => {
        console.error("Cities fetch error:", error);
        setCities([]);
      });
  }, [country]);

  useEffect(() => {
    setDistrict("");
    if (!city) {
      setDistricts([]);
      return;
    }
    client
      .get(`/public/districts/?city=${urlToId(city)}`)
      .then((resp) => setDistricts(resp.data))
      .catch(error => {
        console.error("Districts fetch error:", error);
        setDistricts([]);
      });
  }, [city]);

  const [options, setOptions] = useState([]);

  useEffect(() => {
    client
      .options("/projects/")
      .then((response) => setOptions(response.data.actions.POST.kind.choices))
      .catch(error => {
        console.error("Options fetch error:", error);
      });
  }, []);

  return (
    <Modal isOpen={disclosure?.isOpen || false} onClose={disclosure?.onClose || (() => { })} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Yeni Proje Oluştur</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Box position={"relative"}>
            <Stack
              bg={cardColor}
              rounded={"xl"}
              minW={{ base: "340px", sm: "440px", md: "540px" }}
              p={{ base: 3, sm: 6, md: 8 }}
              spacing={{ base: 2 }}
              maxW={{ lg: "lg" }}
            >
              <Stack spacing={4}>
                <Heading
                  textAlign={"center"}
                  color={"gray.800"}
                  lineHeight={1.1}
                  fontSize={{ base: "2xl", sm: "3xl", md: "4xl" }}
                >
                  Yeni Proje Ekle
                </Heading>
                <Box as={"form"} mt={10}>
                  <Stack>
                    <FormControl isRequired>
                      <FormLabel color={textColor} fontWeight="semibold">Proje Adı:</FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FiHome} color={placeholderColor} />
                        </InputLeftElement>
                        <Input
                          placeholder={"Proje adı giriniz"}
                          type={"text"}
                          value={projectName}
                          onChange={(e) => setProjectName(e.target.value)}
                          bg={inputBg}
                          border="1px solid"
                          borderColor={borderColor}
                          color={textColor}
                          _placeholder={{ color: placeholderColor }}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.500',
                            bg: inputFocusBg,
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                          }}
                          borderRadius="12px"
                          h="48px"
                          pl="40px"
                        />
                      </InputGroup>
                      {errorData.name &&
                        errorData.name.map((e) => (
                          <FormHelperText key={e} color="red">
                            {e}
                          </FormHelperText>
                        ))}
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel color={textColor} fontWeight="semibold">Proje tipi:</FormLabel>
                      <Select
                        bg={inputBg}
                        border="1px solid"
                        borderColor={borderColor}
                        color={textColor}
                        _hover={{ borderColor: 'blue.300' }}
                        _focus={{
                          borderColor: 'blue.500',
                          bg: inputFocusBg,
                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                        }}
                        borderRadius="12px"
                        h="48px"
                        value={kind}
                        onChange={(e) => setKind(e.target.value)}
                        placeholder="Proje tipi seçiniz"
                      >
                        {options.map((_option) => {
                          return (
                            <option key={_option.value} value={_option.value}>
                              {translateProjectKind(_option.value)}
                            </option>
                          );
                        })}
                      </Select>
                      {errorData.kind &&
                        errorData.kind.map((e) => (
                          <FormHelperText key={e} color="red">
                            {e}
                          </FormHelperText>
                        ))}
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel color={textColor} fontWeight="semibold">Daire sayısı:</FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FiHash} color={placeholderColor} />
                        </InputLeftElement>
                        <Input
                          value={numOfUnits}
                          onChange={(e) => setNumOfUnits(e.target.value)}
                          placeholder={"Projede bulunan daire sayısını giriniz"}
                          type="number"
                          min="1"
                          bg={inputBg}
                          border="1px solid"
                          borderColor={borderColor}
                          color={textColor}
                          _placeholder={{ color: placeholderColor }}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.500',
                            bg: inputFocusBg,
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                          }}
                          borderRadius="12px"
                          h="48px"
                          pl="40px"
                        />
                      </InputGroup>
                      {errorData.nof_units &&
                        errorData.nof_units.map((e) => (
                          <FormHelperText key={e} color="red">
                            {e}
                          </FormHelperText>
                        ))}
                    </FormControl>
                    <HStack spacing={4}>
                      <FormControl isRequired>
                        <FormLabel color={textColor} fontWeight="semibold">Ülke</FormLabel>
                        <Select
                          bg={inputBg}
                          border="1px solid"
                          borderColor={borderColor}
                          color={textColor}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.500',
                            bg: inputFocusBg,
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                          }}
                          borderRadius="12px"
                          h="48px"
                          value={country}
                          onChange={(e) => setCountry(e.target.value)}
                          placeholder="Ülke seçiniz"
                        >
                          {countries.map((country) => (
                            <option key={country.url} value={country.url}>
                              {country.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel color={textColor} fontWeight="semibold">İl</FormLabel>
                        <Select
                          bg={inputBg}
                          border="1px solid"
                          borderColor={borderColor}
                          color={textColor}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.500',
                            bg: inputFocusBg,
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                          }}
                          borderRadius="12px"
                          h="48px"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="İl seçiniz"
                          isDisabled={!country}
                        >
                          {cities.map((region) => (
                            <option key={region.url} value={region.url}>
                              {region.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel color={textColor} fontWeight="semibold">İlçe</FormLabel>
                        <Select
                          bg={inputBg}
                          border="1px solid"
                          borderColor={borderColor}
                          color={textColor}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.500',
                            bg: inputFocusBg,
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                          }}
                          borderRadius="12px"
                          h="48px"
                          value={district}
                          onChange={(e) => setDistrict(e.target.value)}
                          placeholder="İlçe seçiniz"
                          isDisabled={!city}
                        >
                          {districts.map((subRegion) => (
                            <option key={subRegion.url} value={subRegion.url}>
                              {subRegion.name}
                            </option>
                          ))}
                        </Select>
                        {errorData.district &&
                          errorData.district.map((e) => (
                            <FormHelperText key={e} color="red">
                              {e}
                            </FormHelperText>
                          ))}
                      </FormControl>
                    </HStack>
                    <FormControl isRequired>
                      <FormLabel color={textColor} fontWeight="semibold">Adres:</FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FiMapPin} color={placeholderColor} />
                        </InputLeftElement>
                        <Input
                          placeholder={
                            "mahalle, cadde, sokak, kapı numarası ve diğer bilgileri giriniz"
                          }
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          bg={inputBg}
                          border="1px solid"
                          borderColor={borderColor}
                          color={textColor}
                          _placeholder={{ color: placeholderColor }}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.500',
                            bg: inputFocusBg,
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                          }}
                          borderRadius="12px"
                          h="48px"
                          pl="40px"
                        />
                      </InputGroup>
                      <Box mt={3}>
                        <MapBoxField value={address} onChangeAddress={setAddress} />
                      </Box>
                      {errorData.address &&
                        errorData.address.map((e) => (
                          <FormHelperText key={e} color="red">
                            {e}
                          </FormHelperText>
                        ))}
                    </FormControl>
                    <FormControl>
                      <FormLabel color={textColor} fontWeight="semibold">Yetkili kişi ad soyad:</FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FiUser} color={placeholderColor} />
                        </InputLeftElement>
                        <Input
                          placeholder={"Proje yetkilisinin ad soyadını giriniz"}
                          type={"text"}
                          value={contactName}
                          onChange={(e) => setContactName(e.target.value)}
                          bg={inputBg}
                          border="1px solid"
                          borderColor={borderColor}
                          color={textColor}
                          _placeholder={{ color: placeholderColor }}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.500',
                            bg: inputFocusBg,
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                          }}
                          borderRadius="12px"
                          h="48px"
                          pl="40px"
                        />
                      </InputGroup>
                      {errorData.contact_name &&
                        errorData.contact_name.map((e) => (
                          <FormHelperText key={e} color="red">
                            {e}
                          </FormHelperText>
                        ))}
                    </FormControl>
                    <FormControl>
                      <FormLabel color={textColor} fontWeight="semibold">Yetkili kişi eposta:</FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FiMail} color={placeholderColor} />
                        </InputLeftElement>
                        <Input
                          placeholder={"Proje yetkilisine ait e-posta adresini giriniz"}
                          type={"email"}
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                          bg={inputBg}
                          border="1px solid"
                          borderColor={borderColor}
                          color={textColor}
                          _placeholder={{ color: placeholderColor }}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.500',
                            bg: inputFocusBg,
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                          }}
                          borderRadius="12px"
                          h="48px"
                          pl="40px"
                        />
                      </InputGroup>
                      {errorData.contact_email &&
                        errorData.contact_email.map((e) => (
                          <FormHelperText key={e} color="red">
                            {e}
                          </FormHelperText>
                        ))}
                    </FormControl>
                    <FormControl>
                      <FormLabel color={textColor} fontWeight="semibold">Yetkili kişi iletişim numarası:</FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FiPhone} color={placeholderColor} />
                        </InputLeftElement>
                        <Input
                          placeholder={
                            "Proje yetkilisine ait iletişim numarasını giriniz (5xxxxxxxxx)"
                          }
                          type={"tel"} // Changed from "number" to "tel"
                          value={contactPhone}
                          onChange={(e) => setContactPhone(e.target.value)}
                          pattern="[0-9]{10}" // Add pattern constraint
                          maxLength={10} // Add max length
                          bg={inputBg}
                          border="1px solid"
                          borderColor={borderColor}
                          color={textColor}
                          _placeholder={{ color: placeholderColor }}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.500',
                            bg: inputFocusBg,
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                          }}
                          borderRadius="12px"
                          h="48px"
                          pl="40px"
                        />
                      </InputGroup>
                      {errorData.contact_phone &&
                        errorData.contact_phone.map((e) => (
                          <FormHelperText key={e} color="red">
                            {e}
                          </FormHelperText>
                        ))}
                    </FormControl>
                    {errorData.general && (
                      <FormHelperText color="red">
                        {errorData.general.join(', ')}
                      </FormHelperText>
                    )}
                    <Button
                      fontFamily={"heading"}
                      mt={8}
                      w={"full"}
                      bgGradient="linear(to-r, red.900,red.700)"
                      color={"white"}
                      _hover={{
                        bgGradient: "linear(to-r, red.800,red.700)",
                        boxShadow: "xl",
                      }}
                      isLoading={loading}
                      loadingText="Oluşturuluyor.."
                      onClick={handleProjectCreate}
                    >
                      Oluştur
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export function ProjectCreate() {
  return (
    <Box height={"100vh"} position={"relative"}>
      <Container
        display={"flex"}
        alignContent={"space-around"}
        flexDirection={"column"}
        flexWrap={"wrap"}
        maxW={"7xl"}
        columns={{ base: 1, md: 1 }}
      >
        <Stack spacing={4}>
          <AddProject />
        </Stack>
      </Container>
    </Box>
  );
}